import { useEffect, useState, useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts'
import { Mail, FileSignature, Cpu, Phone, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import type { UsageStats, UsageCounterRow } from '../../types'

// ── KPI card ─────────────────────────────────────────────────────────────────

interface KpiProps {
  label: string
  value: number
  icon: React.ReactNode
  color: string
}
function KpiCard({ label, value, icon, color }: KpiProps) {
  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 flex items-center gap-4`}
    >
      <div className={`p-3 rounded-lg ${color}`}>{icon}</div>
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value.toLocaleString()}</p>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Tab = 'global' | 'by_erp'

export default function AdminUsage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('global')
  const [stats, setStats] = useState<UsageStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [months, setMonths] = useState(12)

  const load = async () => {
    setLoading(true)
    try {
      const data = await api.getUsageStats({ months })
      setStats(data)
    } catch {
      // silently — table may not exist yet before schema upgrade
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [months]) // eslint-disable-line react-hooks/exhaustive-deps

  // Format by_month for recharts (short month label)
  const chartData = useMemo(() => {
    if (!stats?.by_month) return []
    return stats.by_month.map((row) => ({
      ...row,
      label: row.year_month ?? '',
    }))
  }, [stats])

  const hasByErp = stats?.by_erp && stats.by_erp.length > 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('adminUsage.title', 'Consommation')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {t('adminUsage.subtitle', 'Suivi des usages par type et par fournisseur')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value={3}>3 {t('adminUsage.months', 'mois')}</option>
            <option value={6}>6 {t('adminUsage.months', 'mois')}</option>
            <option value={12}>12 {t('adminUsage.months', 'mois')}</option>
            <option value={24}>24 {t('adminUsage.months', 'mois')}</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-40"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* KPI Summary */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label={t('adminUsage.esignature', 'Signatures électroniques')}
            value={stats.summary.esignature}
            icon={<FileSignature size={20} className="text-blue-600" />}
            color="bg-blue-100 dark:bg-blue-900/30"
          />
          <KpiCard
            label={t('adminUsage.email', 'Emails envoyés')}
            value={stats.summary.email}
            icon={<Mail size={20} className="text-green-600" />}
            color="bg-green-100 dark:bg-green-900/30"
          />
          <KpiCard
            label={t('adminUsage.aiTokensClaude', 'Tokens Claude IA')}
            value={stats.summary.ai_tokens_claude}
            icon={<Cpu size={20} className="text-purple-600" />}
            color="bg-purple-100 dark:bg-purple-900/30"
          />
          <KpiCard
            label={t('adminUsage.aiTokensVapi', 'Appels Vapi (voix)')}
            value={stats.summary.ai_tokens_vapi}
            icon={<Phone size={20} className="text-amber-600" />}
            color="bg-amber-100 dark:bg-amber-900/30"
          />
        </div>
      )}

      {/* Tabs */}
      {hasByErp && (
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
          {(['global', 'by_erp'] as Tab[]).map((t2) => (
            <button
              key={t2}
              onClick={() => setTab(t2)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t2
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t2 === 'global'
                ? t('adminUsage.tabGlobal', 'Vue globale')
                : t('adminUsage.tabByErp', 'Par fournisseur')}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-16 text-gray-400">
          <RefreshCw size={24} className="animate-spin" />
        </div>
      )}

      {!loading && stats && (tab === 'global' || !hasByErp) && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-4">
            {t('adminUsage.chartTitle', 'Évolution mensuelle')}
          </h2>
          {chartData.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">
              {t('adminUsage.noData', 'Aucune donnée sur la période sélectionnée.')}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-gray-200 dark:stroke-gray-700"
                />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="esignature"
                  name={t('adminUsage.esignature', 'Signatures')}
                  fill="#3b82f6"
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  dataKey="email"
                  name={t('adminUsage.email', 'Emails')}
                  fill="#22c55e"
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  dataKey="ai_tokens_claude"
                  name={t('adminUsage.aiTokensClaude', 'Tokens Claude')}
                  fill="#a855f7"
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  dataKey="ai_tokens_vapi"
                  name={t('adminUsage.aiTokensVapi', 'Appels Vapi')}
                  fill="#f59e0b"
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Per-ERP table */}
      {!loading && tab === 'by_erp' && hasByErp && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Mobile card layout */}
          <ul className="divide-y divide-gray-100 dark:divide-gray-700 sm:hidden">
            {stats!.by_erp!.map((row: UsageCounterRow) => (
              <li key={row.erp_id} className="p-4">
                <p className="font-medium text-gray-900 dark:text-white mb-2">{row.erp_name}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                  <span className="text-gray-400 dark:text-gray-500">
                    {t('adminUsage.esignature', 'Signatures')}
                  </span>
                  <span className="text-right font-mono">{row.esignature.toLocaleString()}</span>
                  <span className="text-gray-400 dark:text-gray-500">
                    {t('adminUsage.email', 'Emails')}
                  </span>
                  <span className="text-right font-mono">{row.email.toLocaleString()}</span>
                  <span className="text-gray-400 dark:text-gray-500">
                    {t('adminUsage.aiTokensClaude', 'Tokens Claude')}
                  </span>
                  <span className="text-right font-mono">
                    {row.ai_tokens_claude.toLocaleString()}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500">
                    {t('adminUsage.aiTokensVapi', 'Vapi')}
                  </span>
                  <span className="text-right font-mono">
                    {row.ai_tokens_vapi.toLocaleString()}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          {/* Desktop table layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('adminUsage.supplier', 'Fournisseur')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('adminUsage.esignature', 'Signatures')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('adminUsage.email', 'Emails')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('adminUsage.aiTokensClaude', 'Tokens Claude')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('adminUsage.aiTokensVapi', 'Vapi')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {stats!.by_erp!.map((row: UsageCounterRow) => (
                  <tr key={row.erp_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                      {row.erp_name}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                      {row.esignature.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                      {row.email.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                      {row.ai_tokens_claude.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                      {row.ai_tokens_vapi.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
